import { parseApiResponse, savingFundSchema, savingsBalanceSchema } from "@/api/responseSchemas";
import { apiClient } from "@/api/client";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuthStore } from "@/stores/useAuthStore";
import { captureEntityMutation } from "@/analytics/posthog";

export interface SavingPlan {
  id: number;
  user_id: number;
  name: string;
  description?: string;
  initial_amount: number;
  target_amount: number;
  target_date?: string;
  expected_monthly_payment?: number;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

export interface SavingFund {
  id: number;
  name: string;
  description?: string;
  initial_amount?: number;
  target_amount: number;
  target_date?: string;
  expected_monthly_payment?: number;
  amount?: number;
}

interface SavingPlanInput {
  name: string;
  description?: string;
  initial_amount: number;
  target_amount: number;
  target_date?: string;
}

export interface SavingPlanBalance {
  id: number;
  name: string;
  target_amount: number;
  initial_amount?: number;
  saved_amount: number;
  target_date?: string;
  expected_monthly_payment?: number;
}

export interface SavingsBalance {
  total_saved: number;
  monthly_expected: number;
  saving_plans_balance: SavingPlanBalance[];
}

type SavingsQueryParams = Record<string, string | number | boolean>;

// Query hooks
export const useSavings = (params?: SavingsQueryParams, options?: { enabled?: boolean }) => {
  const token = useAuthStore((s) => s.token);

  return useQuery({
    queryKey: ["savings", params ?? {}],
    queryFn: async () => {
      const res = await apiClient.get("/savings/balance", {
        params,
      });
      return parseApiResponse(savingsBalanceSchema, res.data, "GET /savings/balance");
    },
    enabled: !!token && (options?.enabled ?? true),
  });
};

export const useSavingsFunds = () => {
  const token = useAuthStore((s) => s.token);

  return useQuery({
    queryKey: ["savingsFunds"],
    queryFn: async () => {
      const res = await apiClient.get("/savings/");
      return parseApiResponse(savingFundSchema.array(), res.data, "GET /savings");
    },
    enabled: !!token,
  });
};

// Mutation hooks
export const useCreateSaving = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (savingData: SavingPlanInput) => {
      const res = await apiClient.post("/savings/", savingData);
      return res.data;
    },
    onSuccess: () => {
      captureEntityMutation({ entity: "saving", operation: "create", result: "success" });
      queryClient.invalidateQueries({ queryKey: ["savings"] });
      queryClient.invalidateQueries({ queryKey: ["savingsFunds"] });
    },
    onError: (error) => {
      captureEntityMutation({ entity: "saving", operation: "create", result: "failure", error });
    },
  });
};

export const useUpdateSaving = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: SavingPlanInput }) => {
      const res = await apiClient.patch(`/savings/${id}`, data);
      return res.data;
    },
    onSuccess: () => {
      captureEntityMutation({ entity: "saving", operation: "update", result: "success" });
      queryClient.invalidateQueries({ queryKey: ["savings"] });
      queryClient.invalidateQueries({ queryKey: ["savingsFunds"] });
    },
    onError: (error) => {
      captureEntityMutation({ entity: "saving", operation: "update", result: "failure", error });
    },
  });
};

export const useDeleteSaving = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (savingId: number) => {
      const res = await apiClient.delete(`/savings/${savingId}`);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["savings"] });
      queryClient.invalidateQueries({ queryKey: ["savingsFunds"] });
    },
  });
};
